/**
 * Protokolltabelle der Uebertragungs-Gesamtuebersicht (D-03, D-05, D-24; Sketch 025 `logTable`/
 * `wireRows`/`emptyHtml`). Zeigt Zeitpunkt, Auswertung + Bereich (entfaellt bei `scoped`), Zeitraum,
 * Zeilen, Status und Person -- alle Texte fertig vom Server, keine Datums-/Zahlformatierung im
 * Browser. Eine Zeile oeffnet per Klick oder Enter/Leertaste die Seitenleiste (`onSelect`).
 */
import type { KeyboardEvent } from 'react';
import { Box, Card, Table } from '@mantine/core';
import { TransferStatus } from './TransferStatus';
import type { TransferEntry } from '../../types/statistikPortalTransfer';
import classes from './TransferList.module.css';

export interface TransferListProps {
  entries: TransferEntry[];
  scoped: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
}

function EmptyState({ scoped }: { scoped: boolean }) {
  if (scoped) {
    return (
      <div className={classes.empty}>
        <b>Noch keine Übertragung</b>
        <span>
          Sobald jemand diese Auswertung ans Statistikportal überträgt, steht der Vorgang hier – auch
          wenn er fehlschlägt.
        </span>
      </div>
    );
  }

  return (
    <div className={classes.empty}>
      <b>Noch nichts übertragen</b>
      <span>
        Übertragungen starten auf der jeweiligen Auswertungsseite über „An Statistik-Portal
        übertragen“. Jeder Versuch erscheint hier, erfolgreich oder nicht.
      </span>
    </div>
  );
}

export function TransferList({ entries, scoped, selectedId, onSelect }: TransferListProps) {
  if (entries.length === 0) {
    return (
      <Card withBorder radius="md">
        <EmptyState scoped={scoped} />
      </Card>
    );
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, id: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(id);
    }
  };

  return (
    <Card>
      <Box style={{ overflowX: 'auto' }}>
        <Table highlightOnHover striped="even">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Zeitpunkt</Table.Th>
              {!scoped && <Table.Th>Auswertung</Table.Th>}
              <Table.Th>Zeitraum</Table.Th>
              <Table.Th className={classes.colRight}>Zeilen</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Person</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {entries.map((entry) => (
              <Table.Tr
                key={entry.id}
                tabIndex={0}
                role="button"
                aria-selected={entry.id === selectedId}
                className={`${classes.row} ${entry.id === selectedId ? classes.rowSelected : ''}`}
                onClick={() => onSelect(entry.id)}
                onKeyDown={(event) => handleKeyDown(event, entry.id)}
              >
                <Table.Td>
                  {entry.dateLabel}
                  <span className={classes.sub}>{entry.timeLabel}</span>
                </Table.Td>
                {!scoped && (
                  <Table.Td className={entry.status === 'failed' ? classes.mutedText : undefined}>
                    {entry.evaluationLabel}
                    <span className={classes.sub}>{entry.areaLabel}</span>
                  </Table.Td>
                )}
                <Table.Td>{entry.periodLabel}</Table.Td>
                <Table.Td className={classes.colRight}>{entry.rowCount}</Table.Td>
                <Table.Td>
                  <TransferStatus
                    status={entry.status}
                    statusLabel={entry.statusLabel}
                    changedSince={entry.changedSince}
                    errorSummary={entry.errorSummary}
                  />
                </Table.Td>
                <Table.Td>{entry.personLabel}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>
    </Card>
  );
}
